import { useEffect, useState } from 'react'

import { getProposalContext } from '../utils/helpers/proposal.helpers'

/**
 * The moderation context of an experience's pending proposals (05.4): the
 * proposer names, and — implicitly — whether the caller may moderate at all.
 *
 * One call answers both questions: get-proposal-context is gated on the
 * `moderate` capability, so a 200 means "show the controls" and a 403 means
 * "render none" (AC-6: absent, not disabled). The check rides the CAPABILITY,
 * not `owner_id === user.id`: the difference between an authorization and a
 * coincidence.
 *
 * `enabled` is meant to be `pendingCount > 0`: with an empty queue there is
 * nothing to contextualize and NO network call at all — the normal, permanent
 * case for almost every experience.
 */
export const useProposalContext = ({
  experienceId,
  enabled,
}: {
  experienceId?: string
  enabled: boolean
}): { canModerate: boolean; proposers: Record<string, string> } => {
  const [canModerate, setCanModerate] = useState(false)
  const [proposers, setProposers] = useState<Record<string, string>>({})

  useEffect(() => {
    if (!experienceId || !enabled) return

    let alive = true

    getProposalContext({ experienceId }).then((result) => {
      if (!alive) return
      setCanModerate(result.canModerate)
      setProposers(result.proposers)
    })

    return () => {
      alive = false
    }
  }, [experienceId, enabled])

  return { canModerate, proposers }
}
